import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../lib/api";
import { CheckCircle, WhatsappLogo } from "@phosphor-icons/react";
import { TREATMENTS, CLINIC } from "../data/site";

export const EnquiryForm = ({ defaultTreatment = "", defaultType = "consultation", onSuccess }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", treatment: defaultTreatment, message: "" });
  const [type, setType] = useState(defaultType);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, enquiry_type: type });
      setSent({ ...form, type });
      setForm({ name: "", email: "", phone: "", treatment: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    const sentLabel = sent.type === "consultation" ? "Free 15-minute consultation" : "Appointment enquiry";
    const waText = encodeURIComponent(
      `Hello DK Aesthetics! New website enquiry (${sentLabel})\nName: ${sent.name}\nEmail: ${sent.email}\nPhone: ${sent.phone || "-"}\nTreatment: ${sent.treatment || "Not specified"}\nMessage: ${sent.message}`
    );
    return (
      <div className="border border-primary/25 bg-accent/40 p-8 text-center" data-testid="enquiry-success-panel">
        <CheckCircle size={40} weight="fill" className="mx-auto text-primary" />
        <h3 className="mt-4 font-display text-3xl font-light tracking-tight">Enquiry on its way</h3>
        <p className="mx-auto mt-3 max-w-sm text-sm font-light leading-relaxed text-muted-foreground">
          We've emailed the clinic and we'll reply personally. For the fastest response, send the same
          enquiry straight to our WhatsApp:
        </p>
        <a
          href={`${CLINIC.whatsapp}?text=${waText}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="enquiry-whatsapp-send-button"
          className="mt-6 flex w-full items-center justify-center gap-2 bg-[#25D366] px-6 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#1fb959]"
        >
          <WhatsappLogo size={17} weight="fill" /> Send via WhatsApp
        </a>
        <button
          onClick={() => {
            setSent(null);
            onSuccess?.();
          }}
          data-testid="enquiry-done-button"
          className="mt-3 w-full border border-border px-6 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 hover:border-primary hover:text-primary"
        >
          Done
        </button>
      </div>
    );
  };

  return (
    <div>
      <div className="flex gap-2" role="tablist">
        {[
          ["consultation", "Free consultation"],
          ["appointment", "Appointment"],
        ].map(([v, label]) => (
          <button
            key={v}
            type="button"
            data-testid={`enquiry-type-${v}`}
            onClick={() => setType(v)}
            className={`flex-1 border px-3 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
              type === v ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="mt-5 space-y-4" data-testid="enquiry-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="enq-name" className="text-xs uppercase tracking-[0.15em]">Name</Label>
            <Input id="enq-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className="rounded-none" placeholder="Your full name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="enq-email" className="text-xs uppercase tracking-[0.15em]">Email</Label>
            <Input id="enq-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} className="rounded-none" placeholder="you@email.com" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="enq-phone" className="text-xs uppercase tracking-[0.15em]">Phone</Label>
            <Input id="enq-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} className="rounded-none" placeholder="Optional" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em]">Treatment of interest</Label>
            <Select value={form.treatment} onValueChange={(v) => setForm((f) => ({ ...f, treatment: v }))}>
              <SelectTrigger data-testid="enquiry-treatment-select" className="rounded-none">
                <SelectValue placeholder="Choose a treatment" />
              </SelectTrigger>
              <SelectContent className="max-h-64 rounded-none">
                {TREATMENTS.map((t) => (
                  <SelectItem key={t.id} value={t.name} data-testid={`enquiry-treatment-${t.id}`}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="enq-message" className="text-xs uppercase tracking-[0.15em]">Message</Label>
          <Textarea id="enq-message" data-testid="enquiry-message-input" required rows={4} value={form.message} onChange={set("message")} className="rounded-none" placeholder="Tell us a little about what you'd like to achieve…" />
        </div>
        <button
          type="submit"
          data-testid="enquiry-submit-button"
          disabled={sending}
          className="w-full bg-primary px-6 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-primary-foreground transition-colors duration-300 hover:bg-primary/90 disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send enquiry"}
        </button>
        <p className="text-center text-xs text-muted-foreground">We reply personally — usually within one working day.</p>
      </form>
    </div>
  );
};
